<template>
  <button
    v-tooltip="tooltipCofig"
    type="button"
    :class="[
      'delivery-partner',
      { 'delivery-partner--loading': isLoading },
      { 'delivery-partner--is-ctf-careem': isCtfCareem },
      {
        'delivery-partner--disabled':
          isDisabledByCardOnDelivery || !hasDeliveryCoverage,
      },
    ]"
    data-test="create-delivery"
    @click.prevent="createDelivery"
  >
    <span v-if="isLoading" class="delivery-partner__loading"></span>
    <template v-else>
      <span v-if="displayDeliveryPartnerLabel" class="delivery-partner__name">
        <img
          v-if="logoPartner"
          class="delivery-partner__logo"
          :src="logoPartner"
          :alt="partner"
        />
        <span v-if="isCtfCareem">
          {{ $t("c.order.ctf_careem.request_driver") }}
          <small class="delivery-partner__desc">
            {{ careemDesc }}
          </small>
        </span>
        <span v-else>{{ deliveryPartner }}</span>
      </span>
      <slot v-else />
    </template>
  </button>
</template>

<script>
import {
  deliveryPartners,
  deliveryPartnersDash,
} from "@/utils/enums/delivery-partners";
import { PaymentMethods } from "../../enums";
import { Toast } from "@/design-system";
import Orders from "@/api/orders";
import { validateDeliveryCoverage } from "@/v2/repo/order/validate-delivery-coverage";
import { watchFlag, unWatchFlag } from "@/v2/core/feature-flag";
import { mapGetters } from "vuex";
import { report } from "@chatfood/bug-reporter";

export default {
  name: "DeliveryPartner",
  props: {
    order: {
      type: Object,
      required: true,
    },
    partner: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    hasDeliveryCoverage: true,
    validateCareemPolygon: false,
    careemPolygonFlag: "careem-polygon",
  }),
  computed: {
    ...mapGetters({
      outlets: "outlets/getData",
    }),
    isOwnDelivery() {
      return this.partner === "own-delivery";
    },
    isOther() {
      return this.partner === "other";
    },
    isCtfCareem() {
      return this.partner === "ctf-careem";
    },
    careemDesc() {
      return this.isDisabledByCardOnDelivery
        ? this.$t("c.order.ctf_careem.order_not_prepaid")
        : this.$t("c.order.ctf_careem.powered_careem");
    },
    displayDeliveryPartnerLabel() {
      return !this.isOwnDelivery && !this.isOther;
    },
    deliveryPartner() {
      return deliveryPartners[this.partner];
    },
    deliveryPartnerDash() {
      return deliveryPartnersDash[this.partner];
    },
    logoPartner() {
      try {
        return require(`../../assets/partners/${this.partner}.png`);
      } catch {
        return null;
      }
    },
    isDisabledByCardOnDelivery() {
      return (
        (this.partner === "careem_box" &&
          this.order.payment_method === PaymentMethods.CARD_DELIVERY) ||
        (this.partner === "ctf-careem" &&
          (this.order.payment_method === PaymentMethods.CARD_DELIVERY ||
            this.order.payment_method === PaymentMethods.CASH_DELIVERY))
      );
    },
    tooltipCofig() {
      if (this.isDisabledByCardOnDelivery) {
        return {
          trigger: "click hover focus",
          container: false,
          content: this.$t(
            this.isCtfCareem
              ? "message.partner_doesnt_accept_card_or_cash_on_delivery"
              : "message.partner_doesnt_accept_card_on_delivery",
            {
              partner: this.deliveryPartner,
            }
          ),
        };
      }

      if (!this.hasDeliveryCoverage) {
        return {
          trigger: "click hover focus",
          content: this.$t("message.partner_doesnt_cover_this_address", {
            partner: this.deliveryPartner,
          }),
        };
      }

      return null;
    },
    outlet() {
      return this.outlets.find((outlet) => outlet.id === this.order.outlet_id);
    },
  },
  watch: {
    validateCareemPolygon: {
      handler(value) {
        if (value && this.isCtfCareem && !this.isDisabledByCardOnDelivery) {
          this.validateDelivery();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    watchFlag(this.careemPolygonFlag, this.updateFlag);
  },
  beforeUnmount() {
    unWatchFlag(this.careemPolygonFlag, this.updateFlag);
  },
  methods: {
    updateFlag(val) {
      this.validateCareemPolygon = val;
    },
    async validateDelivery() {
      try {
        let [hasOutletCoverage, hasOrderCoverage] = await Promise.all([
          validateDeliveryCoverage({
            partner: this.partner,
            coordinates: {
              lat: this.outlet.address.lat,
              lng: this.outlet.address.lng,
            },
          }),
          validateDeliveryCoverage({
            partner: this.partner,
            coordinates: {
              lat: this.order.address.lat,
              lng: this.order.address.lng,
            },
          }),
        ]);

        if (!hasOutletCoverage || !hasOrderCoverage) {
          this.hasDeliveryCoverage = false;
        }
      } catch (e) {
        report(e);
        this.hasDeliveryCoverage = false;
      }
    },
    setIsLoading(loading) {
      this.isLoading = loading;
      this.$emit("setLoading", loading);
    },
    async createDelivery() {
      if (
        this.isOther ||
        this.isLoading ||
        !this.hasDeliveryCoverage ||
        this.isDisabledByCardOnDelivery
      ) {
        return;
      }

      if (this.isOwnDelivery) {
        this.closeModal();
        return;
      }

      this.setIsLoading(true);

      try {
        await Orders.requestDelivery(this.order.id, this.partner);
        this.closeModal();
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);

        new Toast().create({
          type: "error",
          text: this.$t(errorMessage),
        });

        this.closeModal();
      }

      this.setIsLoading(false);
    },

    getErrorMessage(validationError) {
      const businessRuleError = validationError?.error?.code;

      const knownRules = [
        "dropoff_coordinate_is_required",
        "pickup_coordinate_is_required",
        "pending_delivery_job_already_exists",
        "delivery_integration_not_enabled",
        "address_is_invalid",
      ];

      if (!businessRuleError || !knownRules.includes(businessRuleError)) {
        return this.$t("message.partner_general_error", {
          partner: this.deliveryPartner,
          partnerDash: this.deliveryPartnerDash,
        });
      }

      const errorMessage = validationError?.error?.message;
      if (errorMessage && businessRuleError === "address_is_invalid") {
        return this.$t(`business_rules.${businessRuleError}`, {
          error: errorMessage,
        });
      }

      return this.$t(`business_rules.${businessRuleError}`);
    },

    closeModal() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.delivery-partner {
  $self: &;

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  height: 49px;
  font-family: "Inter";
  font-weight: 600;
  -webkit-font-smoothing: antialiased;
  padding: var(--space-14);
  color: var(--color-midnight);
  border-radius: var(--border-radius-2);
  background-color: var(--color-cloudy);
  transition: all 0.2s ease-out;

  &:not(&--loading):not(&--disabled) {
    &:hover {
      color: var(--color-pure-white);
      background-color: var(--color-atlantic);
    }

    &:active {
      color: var(--color-pure-white);
      background-color: var(--color-midnight);
    }
  }

  &:disabled,
  &--disabled {
    cursor: no-drop;
    color: var(--color-rainy);

    #{ $self }__logo {
      opacity: 0.4;
    }
  }

  &--loading {
    cursor: default;
  }

  &__name {
    display: flex;
    align-items: center;
  }

  &__logo {
    display: inline-block;
    width: 21px;
    height: 21px;
    border-radius: 100%;
    margin-right: var(--space-7);
  }

  &__loading {
    width: 20px;
    height: 20px;
    border: 3px solid var(--color-rainy);
    border-radius: 23px;
    clip: rect(0px 23px 46px 6px);
    position: absolute;
    top: 50%;
    left: 50%;
    mask-image: -webkit-gradient(
      linear,
      0 0,
      0 100%,
      from(rgba(131, 140, 165, 0.5)),
      to(var(--color-rainy))
    );
    animation: loading 0.6s linear infinite;
    transform: translate(-50%, -50%);
  }

  &--is-ctf-careem {
    text-align: left;
    color: var(--color-pure-white);
    background-color: #48a23f !important;

    #{ $self } {
      &__logo {
        width: 28px;
        height: 28px;
        margin-right: var(--space-7);
      }

      &__desc {
        display: block;
      }
    }

    &#{ $self }--disabled {
      color: rgba(255, 255, 255, 0.7);

      #{ $self } {
        &__logo {
          opacity: 0.7;
        }

        &__desc {
          font-style: italic;
        }
      }
    }

    #{ $self }__loading {
      border-color: var(--color-pure-white);
    }
  }

  @keyframes loading {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    50% {
      transform: translate(-50%, -50%) rotate(180deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
}
</style>
